/**
 * A basic pub-sub mechanism for sibling component communication
 *
 * TODO - adopt standard flexipage sibling communication mechanism when it's available.
 */

/**
 * Defined events. The component can actually use any string that it wants for the event, as long as the sender and
 * receiver agree on the string. Defining events here, however, helps to assure that the sender and receiver do agree
 * on the event name, since they will be referencing the same string variable.
 */
const eventTypes = {
  SHOW_PDF_PAGE: "show-pdf-page",
  PDF_PAGE_LOADED: "pdf-page-load",
  // ORV231211 86a1n07cx is fired from evertime pdf page has finished rendering 
  TOGGLE_WALLET: 'toggle-wallet',
  // ORV240129 TOGGLE_WALLET is fired from panel.js and it's used to display wallet iframe or to hide it 
  PDF_PAGE_CHANGED: "pdf-page-changed",
  SHOW_THUMBNAILS: "show-thumbnails",
  CHECK_THUMBNAIL_CHECKBOX: "check-thumbnail-checkbox",
  SHOW_TOOLS: "show-tools",
  SHOW_DE_PANEL: "show-de-panel",
  ZOOM_IN: "zoom-in",
  ZOOM_OUT: "zoom-out",
  LOCAL_FILE_UPLOAD: "local-file-upload",
  WINDOW_RESIZE: "window-resize",
  TOGGLE_THUMBNAILS: "toggle-thumbnails",
  LOAD_UNSEEN_PAGES: "load-unseen-pages",
  LOADED_UNSEEN_PAGES: "loaded-unseen-pages",
  PERSONA_UPDATE: "persona-update",
  LEARN_COMPLETE: "learn-complete",
  PUBLISH_ATTACHMENT_FORM_DATA: "publish-attachment-form-data",
  GOOGLE_SIGN_IN_STATUS_UPDATE: "google-sign-in-status-update",
  PDF_LOADED: "pdf-loaded",
  PDF_IMPORTED: "pdf-imported",
  SPLIT_PDF: "split-pdf",
  ROTATE_CURRENT_PAGE: "rotate-current-page",
  BROADCAST_SELECTED_PAGE_NUM: "broadcast-selected-page-num",
  MODAL_POP_UP: 'modal-pop-up',
  DOCTYPE_BROADCAST: 'doctype-broadcast',
  LOAD_PDF: "load-pdf",
  OPEN_ZPDF_SIDEBAR: "open-zpdf-sidebar",
  CLOSE_ZPDF_SIDEBAR: "close-zpdf-sidebar",
  CAPTURE_DATA_DOCUMENT: "capture-data-document",
  REFRESH_TOOLBAR_DATA: "refresh-toolbar-data",
  TOOLBAR_RESIZED: "toolbar-resized",
  DISPLAY_HELP: 'display-help',
  HIDE_HELP: 'hide-help',
  COMPONENT_COMMUNICATION: 'component-communication' // CRN240425 Reserved for communication from utility components to their users
};
const events = {};
const eventQueue = {};
const eventRegistrationClearingQueue = [];

/**
 * Confirm that two page references have the same attributes
 * @param {object} pageRef1 - The first page reference
 * @param {object} pageRef2 - The second page reference
 */
const samePageRef = (pageRef1, pageRef2) => {
  const obj1 = pageRef1.attributes;
  const obj2 = pageRef2.attributes;
  return Object.keys(obj1).concat(Object.keys(obj2)).every(key => {
    return obj1[key] === obj2[key];
  });
};

/**
 * Registers a callback for an event
 * @param {string} eventName - Name of the event to listen for.
 * @param {function} callback - Function to invoke when said event is fired.
 * @param {object} thisArg - The value to be passed as the this parameter to the callback function is bound.
 */
const registerListener = (eventName, callback, thisArg, options = {}) => {
  //create a new registration object
  let eventRegistrationObject = {
    id: eventName + '-' + Math.floor(Math.random() * Date.now()),
    eventName: eventName,
    callback: callback,
    thisArg: thisArg,
    options: options
  };
  console.log("calling registerListener ", eventRegistrationObject);

  // Checking that the listener has a pageRef property. We rely on that property for filtering purpose in fireEvent()
  if (!thisArg.pageRef) {
    throw new Error('pubsub listeners need a "@wire(CurrentPageReference) pageRef" property');
  }
  if (!events[eventName]) {
    events[eventName] = [];
  }
  const duplicate = events[eventName].find(listener => {
    //check if we have already registered for the event with same callback, if yes get the existing eventRegistrationObject
    if (listener.callback === callback && listener.thisArg === thisArg) {
      eventRegistrationObject = listener;
      return true;
    }
  });
  if (!duplicate) {
    events[eventName].push(eventRegistrationObject);
  }

  // Disptach any events after returning so that the returned object is usable.
  setTimeout(() => {
    if (options.handleHistoricEvents && eventQueue[eventName] && eventQueue[eventName].length > 0) {
      //clear any events older than 2 seconds
      eventQueue[eventName] = eventQueue[eventName].filter(event => getTimeDifferenceInSeconds(Date.now(), event.timestamp) < 10);
      //check for historic events and call listener for older functions
      eventQueue[eventName].forEach(event => {
        console.log("old event : ", event);
        if (samePageRef(event.pageRef, thisArg.pageRef)) {
          console.log("executing handler for historic event : ", event);
          callback.call(thisArg, event.payload);
        }
      });
    }
  }, 250);
  return eventRegistrationObject;
};

/**
 * 
 * @param {string} eventName - Name of the event to listen for.
 * @param {function} callback - Function to invoke when said event is fired.
 * @param {object} thisArg - The value to be passed as the this parameter to the callback function is bound.
 * @param {JSON} options - Provide additional options , like listening to historic events
 * @returns 
 */
const registerListenerAndTrack = (eventName, callback, thisArg, options = {}) => {
  let eventRegistrationObject = registerListener(eventName, callback, thisArg, options);
  eventRegistrationClearingQueue.push(eventRegistrationObject);
  return eventRegistrationObject;
};

/**
 * Clear out all the tracked events
 */
const clearTrackedListeners = () => {
  while (eventRegistrationClearingQueue.length > 0) {
    try {
      let eventRegToClear = eventRegistrationClearingQueue.pop();
      console.log("unregistering event : " + eventRegToClear);
      unregisterListener(eventRegToClear.eventName, eventRegToClear, self.pageRef);
    } catch (err) {
      console.error("An unexpected error has occured while clearing event listeners");
    }
  }
};

/**
 * Unregisters a callback for an event
 * @param {string} eventName - Name of the event to unregister from.
 * @param {function} callback - Function to unregister.
 * @param {object} thisArg - The value to be passed as the this parameter to the callback function is bound.
 */
const unregisterListener = (eventName, eventRegistrationObject, thisArg) => {
  console.log("unregistering listener ", events[eventName], eventRegistrationObject.id, eventName);
  if (events[eventName]) {
    console.log("unregistering event - before ", events[eventName]);
    events[eventName] = events[eventName].filter(listener => listener.id !== eventRegistrationObject.id);
    console.log("unregistering event - after ", events[eventName]);
  }
};

/**
 * Unregisters all event listeners bound to an object.
 * @param {object} thisArg - All the callbacks bound to this object will be removed.
 */
const unregisterAllListeners = thisArg => {
  Object.keys(events).forEach(eventName => {
    events[eventName] = events[eventName].filter(listener => listener.thisArg !== thisArg);
  });
};

/**
 * Fires an event to listeners.
 * @param {object} pageRef - Reference of the page that represents the event scope.
 * @param {string} eventName - Name of the event to fire.
 * @param {*} payload - Payload of the event to fire.
 */
const fireEvent = (pageRef, eventName, payload) => {
  if (!eventName) {
    console.warn("fireEvent called with empty or undefined eventName: ", eventName);
    return;
  }
  //if no queue is present for the event, create an empty one
  eventQueue[eventName] = eventQueue[eventName] || [];
  //clear any events older than 2 seconds
  eventQueue[eventName] = eventQueue[eventName].filter(event => getTimeDifferenceInSeconds(Date.now(), event.timestamp) < 2);
  //push the newly fired event in queue
  eventQueue[eventName].push({
    pageRef: pageRef,
    payload: payload,
    timestamp: Date.now()
  });
  if (events[eventName]) {
    const listeners = events[eventName];
    listeners.forEach(listener => {
      if (samePageRef(pageRef, listener.thisArg.pageRef)) {
        try {
          listener.callback.call(listener.thisArg, payload);
        } catch (error) {
          // fail silently
          console.log(error);
        }
      }
    });
  }
  console.log("posting a message ", pageRef, eventName, payload);
  window.parent.postMessage({
    pageRef,
    eventName
    // payload: JSON.parse(JSON.stringify(payload))
  }, "*");
};

/**
 * Fires an event to listeners.
 * @param {datetime} currentTime - Current timestamp
 * @param {datetime} eventFiredTime - timestamp of when the event was fired
 */
const getTimeDifferenceInSeconds = (currentTime, eventFiredTime) => (currentTime - eventFiredTime) / 1000;
window.eventTypesGlobal = eventTypes;
window.registerListenerGlobal = registerListener;
window.unregisterListenerGlobal = unregisterListener;
window.unregisterAllListenersGlobal = unregisterAllListeners;
window.fireEventGlobal = fireEvent;
window.registerListenerAndTrackGlobal = registerListenerAndTrack;
window.clearTrackedListenersGlobal = clearTrackedListeners;
export { eventTypes, registerListener, unregisterListener, unregisterAllListeners, fireEvent, registerListenerAndTrack, clearTrackedListeners };